import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Globe2, Building2, Briefcase, ShieldCheck, Megaphone, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const ICONS = {
  "foreign-consultancy": Globe2,
  construction: Building2,
  "business-solutions": Briefcase,
  "it-cyber-security": ShieldCheck,
  "ads-marketing": Megaphone,
};

// Service keys that navigate to a dedicated page instead of scrolling to contact.
const ROUTE_MAP = {
  "it-cyber-security": "/teknoloji",
  construction: "/insaat",
};

const Services = () => {
  const { dict, t } = useI18n();
  const s = dict.services;

  const handleAnchor = (e) => {
    e.preventDefault();
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-28 bg-[hsl(var(--tbs-cream-50))]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">
            {s.eyebrow}
          </div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl leading-[1.08] text-[hsl(var(--primary))]">
            {s.title}
          </h2>
          <p className="mt-4 text-slate-700 leading-relaxed">{s.lead}</p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {s.items.map((item, idx) => {
            const Icon = ICONS[item.key] || Briefcase;
            const isWide = idx === 4; // last service spans wider on lg
            return (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.45, delay: idx * 0.05 }}
                className={isWide ? "lg:col-span-3" : ""}
              >
                <Card
                  data-testid={`services-card-${item.key}`}
                  className="card-lift h-full bg-white border border-slate-200 rounded-2xl p-7 flex flex-col"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-white relative">
                      <Icon className="h-5.5 w-5.5" />
                      <span className="absolute -inset-0.5 rounded-xl border border-[hsl(var(--accent))]/40" />
                    </div>
                    <span className="text-xs font-semibold tracking-wider text-slate-400">
                      0{idx + 1}
                    </span>
                  </div>

                  <h3 className="mt-5 font-display text-xl md:text-2xl text-[hsl(var(--primary))]">
                    {item.title}
                  </h3>
                  <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{item.desc}</p>

                  <ul className="mt-5 space-y-2">
                    {item.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))]" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto pt-6">
                    <div className="gold-rule" />
                    {ROUTE_MAP[item.key] ? (
                      <Link
                        to={ROUTE_MAP[item.key]}
                        onClick={() => window.scrollTo({ top: 0, behavior: "auto" })}
                        data-testid={`services-card-${item.key}-cta`}
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--primary))] hover:text-[hsl(var(--tbs-navy-800))]"
                      >
                        {s.learnMore}
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    ) : (
                      <a
                        href="#contact"
                        onClick={handleAnchor}
                        data-testid={`services-card-${item.key}-cta`}
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--primary))] hover:text-[hsl(var(--tbs-navy-800))]"
                      >
                        {s.learnMore}
                        <ArrowUpRight className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
